import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { useCollectionMutations } from '@/hooks/useCollectionMutations';
import { useToast } from '@/hooks/useToast';
import { Loader2, FolderPlus } from 'lucide-react';

interface CollectionFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CollectionForm({ open, onOpenChange }: CollectionFormProps) {
  const [identifier, setIdentifier] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');

  const { createCollection, isPending } = useCollectionMutations();
  const { toast } = useToast();

  const handleSubmit = async () => {
    if (!identifier || !title) {
      toast({
        title: 'Missing fields',
        description: 'Please provide both an identifier and title.',
        variant: 'destructive',
      });
      return;
    }

    // Validate identifier (URL-safe, no spaces)
    const slug = identifier.trim().toLowerCase().replace(/\s+/g, '-');

    try {
      await createCollection({
        identifier: slug,
        title,
        description,
      });

      toast({
        title: 'Collection created!',
        description: `"${title}" has been created successfully.`,
      });

      onOpenChange(false);
      setIdentifier('');
      setTitle('');
      setDescription('');
    } catch (error) {
      toast({
        title: 'Failed to create collection',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            <div className="flex items-center space-x-2">
              <FolderPlus className="h-5 w-5 text-pink-500" />
              <span>New Collection</span>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="identifier">Identifier (URL-friendly)</Label>
            <Input
              id="identifier"
              placeholder="my-documentaries"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              disabled={isPending}
            />
            <p className="text-xs text-muted-foreground">
              Used in the URL. Only lowercase letters, numbers, and hyphens.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              placeholder="Documentary Collection"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={isPending}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description (optional)</Label>
            <Textarea
              id="description"
              placeholder="A collection of my favorite documentaries..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              disabled={isPending}
            />
          </div>

          <Button
            onClick={handleSubmit}
            disabled={!identifier || !title || isPending}
            className="w-full bg-pink-500 hover:bg-pink-600"
          >
            {isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              'Create Collection'
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}